export type CastAllowedTypes = 'string' | 'number' | 'boolean';
export type CastTargetType<T extends CastAllowedTypes> = T extends 'number'
  ? number
  : T extends 'boolean'
    ? boolean
    : string;

export const castValueFromString = <T extends CastAllowedTypes>(
  envValue: string,
  type: T
): CastTargetType<T> => {
  if (type === 'boolean') {
    return (envValue === 'true') as CastTargetType<T>;
  } else if (type === 'number') {
    return (envValue ? Number(envValue) : undefined) as CastTargetType<T>;
  } else {
    return (envValue ?? undefined) as CastTargetType<T>;
  }
};

/**
 * Remember these 2 important facts:
 * - run-time resolved values via globalThis.__ENV__ might be of any type, so lets
 * convert it to string for proper casting.
 * - build-time resolved env values are always strings.
 * - env variables with an empty value are resolved as an empty string, so we
 * must treat empty string values as non-defined env var.
 */
const resolveEnvVar =
  <T extends CastAllowedTypes>(type: T) =>
  (
    runtimeEnvKey: string,
    buildtimeEnvValue: string,
    defaultValue?: CastTargetType<T>
  ): CastTargetType<T> => {
    const runtimeEnvValue = globalThis['__ENV__']?.[runtimeEnvKey];
    if (runtimeEnvValue !== undefined) {
      return castValueFromString(String(runtimeEnvValue), type);
    } else if (buildtimeEnvValue) {
      return castValueFromString(buildtimeEnvValue, type);
    } else {
      return defaultValue ?? castValueFromString(undefined, type);
    }
  };

export const resolveBooleanEnvVar = resolveEnvVar('boolean');
export const resolveNumberEnvVar = resolveEnvVar('number');
export const resolveStringEnvVar = resolveEnvVar('string');

export const resolveStringListEnvVar = (
  runtimeEnvKey: string,
  buildtimeEnvValue: string,
  defaultValue: string[] = [],
  separator = '|'
): string[] => {
  const result = resolveStringEnvVar(runtimeEnvKey, buildtimeEnvValue, '');
  return result ? result.split(separator) : (defaultValue ?? []);
};
